import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { HandHeart, Mail, Building2, MessageSquare, Download, LogOut, ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { authApi, API } from "@/lib/api";
import { LOGO } from "@/data/site";

const TABS = [
  { key: "volunteers", label: "Volunteers", icon: HandHeart, cols: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["interest", "Interest"], ["message", "Message"]] },
  { key: "newsletter", label: "Newsletter", icon: Mail, cols: [["name", "Name"], ["email", "Email"]] },
  { key: "sponsors", label: "Sponsors", icon: Building2, cols: [["company", "Company"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Phone"], ["message", "Message"]] },
  { key: "contacts", label: "Messages", icon: MessageSquare, cols: [["name", "Name"], ["email", "Email"], ["message", "Message"]] },
];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function SubmissionsTable({ kind, cols }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let active = true;
    authApi.get(`/admin/submissions?kind=${kind}`).then((r) => { if (active) setRows(r.data); }).catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [kind]);

  const exportCsv = async () => {
    const res = await authApi.get(`/admin/export?kind=${kind}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (rows === null) {
    return <div className="py-16 text-center text-[#475569]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#475569]" data-testid={`count-${kind}`}>{rows.length} {rows.length === 1 ? "entry" : "entries"}</p>
        <Button onClick={exportCsv} disabled={!rows.length} variant="outline" size="sm" className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white" data-testid={`export-${kind}`}>
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="py-16 text-center text-[#94a3b8] border border-dashed border-[#E2E8F0] rounded-xl">No submissions yet.</div>
      ) : (
        <div className="overflow-x-auto border border-[#E2E8F0] rounded-xl">
          <table className="w-full text-sm" data-testid={`table-${kind}`}>
            <thead>
              <tr className="bg-[#F1F5F9] text-left text-[#0A2540]">
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                {cols.map(([, label]) => (<th key={label} className="px-4 py-3 font-semibold whitespace-nowrap">{label}</th>))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id || i} className="border-t border-[#E2E8F0] hover:bg-[#FAFAFA] align-top">
                  <td className="px-4 py-3 whitespace-nowrap text-[#475569]">{fmtDate(row.created_at)}</td>
                  {cols.map(([field]) => (
                    <td key={field} className="px-4 py-3 text-[#0A2540] max-w-xs">{row[field] || <span className="text-[#cbd5e1]">—</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", user: null });

  const checkAuth = useCallback(async () => {
    try {
      const r = await authApi.get("/auth/me");
      if (r.data.is_admin) setState({ status: "ok", user: r.data });
      else setState({ status: "forbidden", user: r.data });
    } catch (e) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = async () => {
    try { await authApi.post("/auth/logout"); } catch (e) { /* ignore */ }
    navigate("/admin/login", { replace: true });
  };

  if (state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#16A34A]" />
      </div>
    );
  }

  if (state.status === "forbidden") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A2540] px-6">
        <div className="max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-[#E85D04] mx-auto" />
          <h1 className="mt-4 font-display font-bold text-2xl text-[#0A2540]">Access denied</h1>
          <p className="mt-2 text-[#475569]">The account <strong>{state.user?.email}</strong> is not authorized to view this dashboard.</p>
          <Button onClick={logout} className="mt-6 bg-[#0A2540] text-white rounded-full h-11 px-6" data-testid="forbidden-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display font-bold text-[#0A2540]">Admin Dashboard</div>
              <div className="text-xs text-[#475569]">{state.user?.email}</div>
            </div>
          </div>
          <Button onClick={logout} variant="outline" size="sm" className="rounded-full" data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0A2540]">Form Submissions</h1>
        <p className="mt-1 text-[#475569]">Volunteer, newsletter, sponsor, and contact entries from your website.</p>

        <Tabs defaultValue="volunteers" className="mt-8">
          <TabsList className="flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0 mb-6">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="rounded-full border border-[#E2E8F0] px-4 py-2 font-semibold text-[#475569] data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=active]:border-[#0A2540]">
                  <Icon className="w-4 h-4 mr-2" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-0">
              <SubmissionsTable kind={t.key} cols={t.cols} />
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
